import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { deleteImage, uploadImages, validateImage } from './imageService'
import type { Enquiry, EnquiryInput } from '../types/admin'

export const enquiriesRef = collection(db, 'enquiries')

/** Midlertidig status mens billederne uploades. Vises ikke i admin. */
export const UPLOADING_STATUS = 'Uploader'

function toEnquiry(snapshot: QueryDocumentSnapshot<DocumentData>): Enquiry {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    name: data.name ?? '',
    phone: data.phone ?? '',
    email: data.email ?? '',
    address: data.address ?? '',
    description: data.description ?? '',
    status: data.status ?? 'Ny henvendelse',
    images: Array.isArray(data.images) ? data.images : [],
    createdAt: timestampToIso(data.createdAt),
    caseId: data.caseId ?? undefined,
  }
}

/**
 * Lytter på henvendelser. Ufærdige henvendelser (status "Uploader") filtreres fra,
 * så halvfærdige indsendelser aldrig dukker op som rigtige henvendelser i admin.
 */
export function subscribeEnquiries(
  onChange: (enquiries: Enquiry[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(enquiriesRef, orderBy('createdAt', 'desc')),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map(toEnquiry)
          .filter((enquiry) => String(enquiry.status) !== UPLOADING_STATUS),
      ),
    onError,
  )
}

export interface SubmitResult {
  /** Dokument-id'et. Genbruges ved nyt forsøg, så der ikke opstår dubletter. */
  draftId: string
}

/**
 * Sikker indsendelse i faste trin:
 * 1) validér alle billeder — intet oprettes hvis en fil er ugyldig
 * 2) opret/genbrug dokumentet med den midlertidige status "Uploader" (skjult i admin)
 * 3) upload billederne til enquiries/{id}/...
 * 4) gem stier og URL'er, og skift først derefter status til "Ny henvendelse"
 *
 * Slår et upload fejl, ryddes de delvist uploadede filer op, dokumentet bliver
 * liggende som ufærdigt (og usynligt i admin), og samme id genbruges ved nyt forsøg.
 */
export async function submitEnquiry(
  input: EnquiryInput,
  existingDraftId?: string,
): Promise<SubmitResult> {
  for (const file of input.files) {
    const problem = validateImage(file)
    if (problem) throw new Error(problem)
  }

  const enquiryRef = existingDraftId
    ? doc(db, 'enquiries', existingDraftId)
    : doc(enquiriesRef)

  await setDoc(
    enquiryRef,
    {
      name: input.name.trim(),
      phone: input.phone.trim(),
      email: input.email.trim(),
      address: input.address.trim(),
      description: input.description.trim(),
      status: UPLOADING_STATUS,
      images: [],
      imagePaths: [],
      createdAt: serverTimestamp(),
    },
    { merge: true },
  )

  if (input.files.length > 0) {
    let uploaded
    try {
      uploaded = await uploadImages(input.files, `enquiries/${enquiryRef.id}`)
    } catch (error) {
      const failure = new Error('upload-failed') as Error & { draftId: string; cause?: unknown }
      failure.draftId = enquiryRef.id
      failure.cause = error
      throw failure
    }

    try {
      await updateDoc(enquiryRef, {
        images: uploaded,
        imagePaths: uploaded.map((image) => image.path),
        status: 'Ny henvendelse',
      })
    } catch (error) {
      await Promise.all(uploaded.map((image) => deleteImage(image.path).catch(() => null)))
      const failure = new Error('upload-failed') as Error & { draftId: string; cause?: unknown }
      failure.draftId = enquiryRef.id
      failure.cause = error
      throw failure
    }
  } else {
    await updateDoc(enquiryRef, { status: 'Ny henvendelse' })
  }

  return { draftId: enquiryRef.id }
}

export async function updateEnquiryDoc(id: string, patch: Partial<Enquiry>): Promise<void> {
  await updateDoc(doc(db, 'enquiries', id), { ...patch, updatedAt: serverTimestamp() })
}

/** Sletter henvendelsen og alle dens billeder i Storage. */
export async function deleteEnquiryDoc(id: string, imagePaths: string[]): Promise<void> {
  await Promise.all(imagePaths.map((path) => deleteImage(path).catch(() => null)))
  await deleteDoc(doc(db, 'enquiries', id))
}
